import React, { FC, useMemo, useEffect, useRef } from "react";
import { Alert, Spin } from "antd";
import Title from "antd/es/typography/Title";
import { useFormikContext } from "formik";
import { skipToken } from "@reduxjs/toolkit/query";
import { FormInput, FormSelect } from "shared/ui";
import {
  adaptPackagesForMultipleExecuteReport,
  adaptMaterialCategories,
} from "shared/adapters";
import {
  useGetAllPackagesQuery,
  useGetAllCompaniesQuery,
  useGetAllOrdersWithNoParamsQuery,
  useGetMaterialCategoriesWithNoParamsQuery,
  BatchPackagingItem,
  PackagingItem,
} from "shared/api";
import { adaptOrders } from "shared/adapters/order/adapter.order";
import { BatchFormValues } from "../types";

interface BatchFormProps {
  clearPackagingsOnOrderChange?: boolean;
  isBatchLoaded?: boolean;
  existingPackagings?: number[] | BatchPackagingItem[];
}

export const BatchForm: FC<BatchFormProps> = ({
  clearPackagingsOnOrderChange = true,
  isBatchLoaded = true,
  existingPackagings = [],
}) => {
  const { values, handleBlur, handleChange, setFieldValue, errors, touched } =
    useFormikContext<BatchFormValues>();

  const prevOrdersRef = useRef<number[]>([]);
  const prevCategoryRef = useRef<number | null>(null);

  const { data: companies, isLoading: isLoadingCompanies } =
    useGetAllCompaniesQuery({});

  const adaptedCompanies = useMemo(
    () => companies?.map((c) => ({ label: c.name, value: c.id })) ?? [],
    [companies]
  );

  const companyId =
    (typeof values.company === "object" && (values.company as any)?.id) ||
    (typeof values.company === "number" && values.company) ||
    null;
  const hasCompany = !!companyId;

  const { data: orders, isFetching: isFetchingOrders } =
    useGetAllOrdersWithNoParamsQuery(
      (hasCompany && { buyer: companyId as number }) || skipToken
    );

  const adaptedOrders = useMemo(() => adaptOrders(orders || []), [orders]);

  const { data: materialCategories, isLoading: isLoadingMaterialCategories } =
    useGetMaterialCategoriesWithNoParamsQuery();

  const adaptedMaterialCategories = useMemo(
    () => adaptMaterialCategories(materialCategories ?? []),
    [materialCategories]
  );

  const { data: packagings, isFetching: isFetchingPackages } =
    useGetAllPackagesQuery(
      (!!values.orders?.length && {
        order__in: values.orders,
        expand: "material,order",
        batches__isnull: true,
        ...(values.material_category
          ? { material__material_category: values.material_category }
          : {}),
      }) ||
        skipToken
    );

  const adaptedPackages = useMemo(() => {
    const uniqueMap = new Map<number, { label: string; value: number }>();

    const existingRaw = (existingPackagings || []).map((p) => {
      if (typeof p === "object" && "packaging" in p) {
        return {
          id: p.packaging.id,
          name: p.packaging.name,
          order: p.packaging.order,
          material: p.packaging.material,
        };
      }
      return {
        id: p as number,
        name: String(p),
        order: 0,
        material: { name: "N/A" },
      };
    });

    const apiRaw = Array.isArray(packagings) ? packagings : [];

    const adapted = adaptPackagesForMultipleExecuteReport([
      ...existingRaw,
      ...apiRaw,
    ] as PackagingItem[]);

    adapted.forEach((item) => {
      uniqueMap.set(item.value, item);
    });

    return Array.from(uniqueMap.values());
  }, [packagings, existingPackagings]);

  // Dependent orders/packagings are reset only on a real user change of the
  // company (see the Company select's onChange), never here — clearing them in
  // an effect that watches `company` races with the edit-mode hydration and
  // wipes the preloaded orders/packagings before they can render.

  useEffect(() => {
    const currentOrders = values.orders ?? [];
    const removedOrders = prevOrdersRef.current.filter(
      (id) => !currentOrders.includes(id)
    );
    if (
      removedOrders.length > 0 &&
      clearPackagingsOnOrderChange &&
      prevOrdersRef.current.length > 0
    ) {
      setFieldValue("packagings", []);
    }
    prevOrdersRef.current = [...currentOrders];
  }, [values.orders, setFieldValue, clearPackagingsOnOrderChange]);

  useEffect(() => {
    const currentCategory = values.material_category ?? null;
    const categoryChanged =
      prevCategoryRef.current !== null &&
      prevCategoryRef.current !== currentCategory;
    if (categoryChanged && clearPackagingsOnOrderChange && isBatchLoaded) {
      setFieldValue("packagings", []);
    }
    prevCategoryRef.current = currentCategory;
  }, [
    values.material_category,
    setFieldValue,
    clearPackagingsOnOrderChange,
    isBatchLoaded,
  ]);

  const isOrdersLoading = isFetchingOrders || !isBatchLoaded;
  const showPackagingSection = !!values.orders?.length;
  const isPackagesLoading =
    isFetchingPackages || (showPackagingSection && isFetchingOrders);

  const companyValue = (isBatchLoaded && companyId) || null;
  const ordersValue = (isBatchLoaded && hasCompany && values.orders) || [];
  const ordersPlaceholder = hasCompany ? "Orders" : "Select company first";

  const companyError =
    (typeof errors.company === "string" && errors.company) || "";
  const orderError =
    (Array.isArray(errors.orders) && errors.orders[0]) ||
    (typeof errors.orders === "string" && errors.orders) ||
    "";
  const packError =
    (Array.isArray(errors.packagings) && errors.packagings[0]) ||
    (typeof errors.packagings === "string" && errors.packagings) ||
    "";

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-1.5">
        <Title level={5} className="!mb-0 !text-sm !font-medium">
          Name
        </Title>
        <FormInput
          value={values.name}
          fieldName="name"
          placeholder="Name"
          handleChange={handleChange}
          handleBlur={handleBlur}
          error={errors.name}
          isError={!!(touched.name && errors.name)}
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <Title level={5} className="!mb-0 !text-sm !font-medium">
          Company
        </Title>
        <FormSelect
          value={companyValue}
          showSearch
          fieldName="company"
          options={adaptedCompanies}
          setFieldValue={(f, v) => {
            const newCompany = (v as number) || null;
            // Reset dependent fields only when the user actually picks a
            // different company; hydration sets `company` directly and must
            // not clear the preloaded orders/packagings.
            if (newCompany !== companyId) {
              setFieldValue("orders", []);
              setFieldValue("packagings", []);
            }
            setFieldValue(f, newCompany);
          }}
          handleBlur={handleBlur}
          placeholder="Company"
          error={companyError}
          isError={!!(touched.company && errors.company)}
          isLoading={isLoadingCompanies}
          disabled={!isBatchLoaded}
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <Title level={5} className="!mb-0 !text-sm !font-medium">
          Description
        </Title>
        <FormInput
          value={values.description}
          fieldName="description"
          placeholder="Description"
          handleChange={handleChange}
          handleBlur={handleBlur}
          error={errors.description}
          isError={!!errors.description}
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <Title level={5} className="!mb-0 !text-sm !font-medium">
          Orders
        </Title>
        <FormSelect
          multiple
          value={ordersValue}
          fieldName="orders"
          options={adaptedOrders}
          setFieldValue={setFieldValue}
          handleBlur={handleBlur}
          placeholder={ordersPlaceholder}
          error={orderError}
          isError={!!(touched.orders && errors.orders)}
          isLoading={isOrdersLoading}
          disabled={!hasCompany || !isBatchLoaded}
          searchHint="If your order is not in the list, please use the search"
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <Title level={5} className="!mb-0 !text-sm !font-medium">
          Material Category
        </Title>
        <FormSelect
          value={(isBatchLoaded && values.material_category) || null}
          showSearch
          allowClear
          fieldName="material_category"
          options={adaptedMaterialCategories}
          setFieldValue={(f, v) => setFieldValue(f, v ?? null)}
          handleBlur={handleBlur}
          placeholder="Material Category (optional)"
          isLoading={isLoadingMaterialCategories}
          disabled={!isBatchLoaded}
        />
      </div>

      {showPackagingSection && (
        <div className="flex flex-col gap-1.5">
          <Title level={5} className="!mb-0 !text-sm !font-medium">
            Packagings
          </Title>
          <div className="relative">
            <Spin spinning={isPackagesLoading} size="small">
              {!isPackagesLoading && adaptedPackages.length === 0 ? (
                <Alert
                  type="warning"
                  message="No available packagings found"
                  className="!mb-0"
                />
              ) : (
                <FormSelect
                  multiple
                  value={values.packagings ?? []}
                  fieldName="packagings"
                  options={adaptedPackages}
                  setFieldValue={setFieldValue}
                  handleBlur={handleBlur}
                  placeholder="Packagings"
                  error={packError}
                  isError={!!errors.packagings}
                  isLoading={isPackagesLoading}
                  disabled={isPackagesLoading}
                />
              )}
            </Spin>
          </div>
        </div>
      )}
    </div>
  );
};
